import { Badge, Button, cn, ModeToggle, Skeleton } from "@devhop/ui";
import { useParams } from "@tanstack/react-router";
import { ChevronDown, ChevronUp, Download, Lock, Trophy } from "lucide-react";
import { useState } from "react";
import { authClient } from "@/modules/auth/presentation/api/client";
import { useEventSummaryQuery } from "@/modules/events/presentation/api/queries";
import { RankIcon } from "@/shared/ui/RankIcon";
import type { TeamResult } from "../api/client";
import { useResultsQuery, useSettingsQuery } from "../api/queries";
import { exportResultsToExcel } from "../lib/exportResults";
import { flattenAllRoundResults } from "../lib/flattenEventResults";

// ─── Sub-score row ────────────────────────────────────────────────────────────

function SubScoreSection({
  label,
  accentClass,
  criteria,
}: {
  label: string;
  accentClass: string;
  criteria: { name: string; value: number; max: number }[];
}) {
  const total = criteria.reduce((s, c) => s + c.value, 0);
  const maxTotal = criteria.reduce((s, c) => s + c.max, 0);

  return (
    <div className="rounded-lg bg-muted/30 p-3">
      <p
        className={cn(
          "mb-2 font-semibold text-xs uppercase tracking-wide",
          accentClass,
        )}
      >
        {label} — {total} / {maxTotal}
      </p>
      <div className="grid grid-cols-3 gap-2">
        {criteria.map((c) => (
          <div key={c.name} className="text-center">
            <p className="text-muted-foreground text-xs leading-tight">
              {c.name}
            </p>
            <p className="font-bold text-foreground tabular-nums">
              {c.value}
              <span className="font-normal text-muted-foreground text-xs">
                /{c.max}
              </span>
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Result Row ───────────────────────────────────────────────────────────────

function ResultRow({ result }: { result: TeamResult }) {
  const [expanded, setExpanded] = useState(false);

  const allSubmitted =
    result.judge1.submitted &&
    result.judge2.submitted &&
    result.judge3.submitted;

  return (
    <>
      <tr
        className={cn(
          "cursor-pointer border-border border-b transition-colors hover:bg-muted/30",
          result.rank === 1 && "bg-chart-5/5",
          result.rank === 2 && "bg-muted/20",
          result.rank === 3 && "bg-chart-3/5",
        )}
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="px-4 py-3 text-center text-xl">
          <RankIcon rank={result.rank} className="min-h-8" />
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-xs tabular-nums">
              #{result.orderNumber}
            </span>
            <span className="font-semibold text-foreground">{result.name}</span>
            {!allSubmitted && (
              <Badge variant="outline" className="text-xs">
                ຍັງບໍ່ຄົບ
              </Badge>
            )}
          </div>
        </td>
        <td className="hidden px-4 py-3 text-center tabular-nums sm:table-cell">
          <span
            className={cn(
              "font-semibold",
              result.judge1.submitted
                ? "text-foreground"
                : "text-muted-foreground",
            )}
          >
            {result.judge1.total}
          </span>
          <span className="text-muted-foreground text-xs">/30</span>
        </td>
        <td className="hidden px-4 py-3 text-center tabular-nums sm:table-cell">
          <span
            className={cn(
              "font-semibold",
              result.judge2.submitted
                ? "text-foreground"
                : "text-muted-foreground",
            )}
          >
            {result.judge2.total}
          </span>
          <span className="text-muted-foreground text-xs">/30</span>
        </td>
        <td className="hidden px-4 py-3 text-center tabular-nums md:table-cell">
          <span
            className={cn(
              "font-semibold",
              result.judge3.submitted
                ? "text-foreground"
                : "text-muted-foreground",
            )}
          >
            {result.judge3.total}
          </span>
          <span className="text-muted-foreground text-xs">/40</span>
        </td>
        <td className="px-4 py-3 text-center">
          <span className="font-extrabold text-foreground text-xl tabular-nums">
            {result.grandTotal}
          </span>
          <span className="text-muted-foreground text-xs">
            /{result.maxTotal}
          </span>
        </td>
        <td className="px-3 py-3 text-center text-muted-foreground">
          {expanded ? (
            <ChevronUp className="inline size-4" />
          ) : (
            <ChevronDown className="inline size-4" />
          )}
        </td>
      </tr>

      {/* Expanded Sub-scores */}
      {expanded && (
        <tr className="border-border border-b bg-muted/10">
          <td colSpan={7} className="px-4 py-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <SubScoreSection
                label="Judge 1 — Dance Skill & Technique"
                accentClass="text-accent"
                criteria={[
                  {
                    name: "Strength & Difficulty",
                    value: result.judge1.strengthDifficulty,
                    max: 10,
                  },
                  {
                    name: "Timing & Control",
                    value: result.judge1.timingControl,
                    max: 10,
                  },
                  {
                    name: "Unity & Synchronizity",
                    value: result.judge1.unitySynchronicity,
                    max: 10,
                  },
                ]}
              />
              <SubScoreSection
                label="Judge 2 — Stage Presence & Performance"
                accentClass="text-chart-3"
                criteria={[
                  {
                    name: "Charisma & Confidence",
                    value: result.judge2.charismaConfidence,
                    max: 10,
                  },
                  {
                    name: "Facial Expression & Emotions",
                    value: result.judge2.facialExpression,
                    max: 10,
                  },
                  {
                    name: "Stage Awareness & Engagement",
                    value: result.judge2.stageAwareness,
                    max: 10,
                  },
                ]}
              />
              <SubScoreSection
                label="Judge 3 — Concept & Creativity"
                accentClass="text-chart-5"
                criteria={[
                  {
                    name: "Concept & Originality",
                    value: result.judge3.conceptOriginality,
                    max: 15,
                  },
                  {
                    name: "Costume, Props & Visual Composition",
                    value: result.judge3.costumeVisual,
                    max: 15,
                  },
                  {
                    name: "Choreography Creativity & Overall Quality",
                    value: result.judge3.choreographyQuality,
                    max: 10,
                  },
                ]}
              />
              {result.extraCriteria.length > 0 ? (
                <SubScoreSection
                  label="Special Criteria"
                  accentClass="text-primary"
                  criteria={result.extraCriteria.map((c) => ({
                    name: c.name,
                    value: c.value,
                    max: c.maxScore,
                  }))}
                />
              ) : null}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── ResultsPage ──────────────────────────────────────────────────────────────

export function ResultsPage() {
  const params = useParams({ strict: false }) as { eventId?: string };
  const eventId = params.eventId;

  const { data: sessionData, isPending: sessionPending } =
    authClient.useSession();
  const isAdmin = sessionData?.user?.role === "competition_admin";

  const {
    data: eventMeta,
    isPending: metaPending,
    isError: metaError,
  } = useEventSummaryQuery(eventId);
  const { data: settings, isLoading: settingsLoading } =
    useSettingsQuery(eventId);
  const {
    data: results,
    isLoading: resultsLoading,
    isError: resultsError,
  } = useResultsQuery(eventId);

  const isPublished = settings?.resultsShown === true;
  const waitingForViewerContext =
    settings !== undefined && settings.resultsShown === false && sessionPending;

  const viewerLockedOut = !sessionPending && !isPublished && !isAdmin;
  const resultsBlocked = resultsError || viewerLockedOut;

  const eventMissing =
    Boolean(eventId) && !metaPending && (metaError || !eventMeta);

  const isLoading =
    metaPending || settingsLoading || resultsLoading || waitingForViewerContext;

  const handleExport = () => {
    if (results && flattenAllRoundResults(results).length > 0) {
      exportResultsToExcel(results, {
        eventName: eventMeta?.name,
        eventSlug: eventMeta?.slug,
      });
    }
  };

  if (!eventId) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-2 bg-background p-6 text-center text-muted-foreground text-sm">
        <p>ບໍ່ພົບລະຫັດງານໃນ URL</p>
        <a className="text-primary underline" href="/competition/events">
          ໄປໜ້າລາຍກຳຜົນທີ່ເປີດສາທາລະນະ
        </a>
      </div>
    );
  }

  return (
    <div className="flex min-h-svh flex-col bg-background">
      {/* Header */}
      <header className="kpop-bg border-border border-b bg-card px-4 py-4 sm:px-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-bold text-accent text-xs uppercase tracking-widest">
              KiDS EXPO 2026
            </p>
            <h1 className="kpop-gradient-text font-extrabold text-2xl leading-tight">
              ຜົນການແຂ່ງຂັນ
            </h1>
            <p className="text-muted-foreground text-sm">
              {eventMeta?.name ?? "…"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isPublished &&
            results &&
            flattenAllRoundResults(results).length > 0 ? (
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download data-icon="inline-start" />
                Export Excel
              </Button>
            ) : null}
            <ModeToggle />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-4 sm:p-6">
        {isLoading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: skeleton
              <Skeleton key={i} className="h-14 w-full rounded-xl" />
            ))}
          </div>
        ) : eventMissing ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Lock className="mb-4 size-12 text-muted-foreground/40" />
            <h2 className="font-bold text-foreground text-xl">ບໍ່ພົບງານນີ້</h2>
            <p className="mt-2 text-muted-foreground text-sm">
              ກວດລິ້ງ ຫຼື ກັບໄປໜ້າລາຍກຳ
            </p>
            <a
              className="mt-4 text-primary text-sm underline"
              href="/competition/events"
            >
              ໜ້າລາຍກຳຜົນ
            </a>
          </div>
        ) : resultsBlocked ? (
          /* Not published yet */
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Lock className="mb-4 size-12 text-muted-foreground/40" />
            <h2 className="font-bold text-foreground text-xl">
              ຜົນຍັງບໍ່ໄດ້ຖືກປະກາດ
            </h2>
            <p className="mt-2 text-muted-foreground text-sm">
              ລໍຖ້າຜູ້ຄຸ້ມຄອງປະກາດຜົນ — ຫຼື ທ່ານບໍ່ມີສິດເບິ່ງກ່ອນເວລາ
            </p>
          </div>
        ) : !results?.divisions.some((s) =>
            s.rounds.some((r) => r.teams.length > 0),
          ) ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Trophy className="mb-4 size-12 text-muted-foreground/40" />
            <p className="text-muted-foreground">ຍັງບໍ່ມີຜົນ</p>
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            {results.divisions.map((section) => (
              <div key={section.divisionId}>
                <h2 className="mb-3 font-bold text-foreground text-lg">
                  {section.divisionName}
                </h2>
                <div className="flex flex-col gap-6">
                  {section.rounds.map((rnd) =>
                    rnd.teams.length === 0 ? null : (
                      <div key={`${section.divisionId}-r${rnd.roundIndex}`}>
                        {section.roundCount > 1 ? (
                          <h3 className="mb-2 font-semibold text-muted-foreground text-sm">
                            ຮອບ {rnd.roundIndex}
                          </h3>
                        ) : null}
                        <div className="overflow-hidden rounded-xl ring-1 ring-border">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-border border-b bg-muted/50 text-left text-muted-foreground">
                                <th className="px-4 py-3 text-center font-medium">
                                  ອັນດັບຮາງ
                                </th>
                                <th className="px-4 py-3 font-medium">ທີມ</th>
                                <th className="hidden px-4 py-3 text-center font-medium sm:table-cell">
                                  J1 (30)
                                </th>
                                <th className="hidden px-4 py-3 text-center font-medium sm:table-cell">
                                  J2 (30)
                                </th>
                                <th className="hidden px-4 py-3 text-center font-medium md:table-cell">
                                  J3 (40)
                                </th>
                                <th className="px-4 py-3 text-center font-medium">
                                  ລວມ
                                </th>
                                <th className="px-3 py-3" />
                              </tr>
                            </thead>
                            <tbody>
                              {rnd.teams.map((r) => (
                                <ResultRow
                                  key={`${section.divisionId}-${rnd.roundIndex}-${r.id}`}
                                  result={r}
                                />
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            ))}
            <p className="text-center text-muted-foreground text-xs">
              ກົດທີ່ແຖວເພື່ອເບິ່ງລາຍລະອຽດຄະແນນ
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-border border-t px-4 py-3 text-center text-muted-foreground text-xs">
        KiDS EXPO 2026
        {eventMeta?.name
          ? ` — ${eventMeta.name}`
          : " — K-POP Cover Dance Open Division"}
      </footer>
    </div>
  );
}
